import React, { useMemo } from 'react';
import { batchActions } from 'redux-batched-actions';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import AsyncIcon from '@atlassian/jira-common-components-async-icon/src/view.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	type MenuNode,
	type MenuItem,
	MenuNodeType,
} from '@atlassian/jira-software-context-menu/src/common/types.tsx';
import type { Priority } from '../../../../../../model/priority/types';
import { useBacklogActionCreator, useBacklogSelector } from '../../../../../../state';
import { cardPrioritySetOptimistic } from '../../../../../../state/actions/priorities';
import { getCards, makeCardKeyFromCardId } from '../../../../../../state/selectors/cards';
import { canBulkEditIssues, canEditIssue } from '../../../../../../state/selectors/permissions';
import { getPriorities } from '../../../../../../state/selectors/priorities';
import { PRIORITY_MENU_ID } from '../../constants';
import messages from '../../messages';
import type { Props, ContentProps } from './types';

export const usePriorityContent = ({
	isPriorityEnabled,
	priorities,
	canChangePriority,
	onClick,
}: ContentProps): MenuNode[] => {
	const { formatMessage } = useIntl();

	if (!isPriorityEnabled || !canChangePriority) return [];

	const priorityOptions: MenuItem[] = priorities.map((value) => ({
		type: MenuNodeType.Item,
		id: `${PRIORITY_MENU_ID}/value__${value.id}`,
		label: value.name,
		icon: <AsyncIcon url={value.iconUrl} />,
		onClick: (analyticsEvent) => onClick(value, analyticsEvent),
	}));

	if (priorityOptions.length === 0) {
		return [];
	}

	return [
		{
			type: MenuNodeType.Item,
			id: PRIORITY_MENU_ID,
			label: formatMessage(messages.priority),
			children: priorityOptions,
		},
	];
};

export const usePriority = ({ selectedCardIds }: Props) => {
	const priorities = useBacklogSelector(getPriorities);
	const cardEntities = useBacklogSelector(getCards);
	const getIssueKey = useBacklogSelector(makeCardKeyFromCardId);

	const canBulkEdit = useBacklogSelector(canBulkEditIssues);
	const canEdit = useBacklogSelector(canEditIssue);
	const canChangePriority = canBulkEdit && canEdit;

	const onPriorityClick = useBacklogActionCreator(
		(priority: Priority, analyticsEvent: UIAnalyticsEvent) =>
			batchActions(
				selectedCardIds.map((id) =>
					cardPrioritySetOptimistic(
						{
							cardId: id,
							priority,
						},
						getIssueKey(id),
						analyticsEvent,
					),
				),
			),
	);

	const isPriorityEnabled = useMemo(
		() => selectedCardIds.some((cardId) => cardEntities[String(cardId)]?.priority),
		[selectedCardIds, cardEntities],
	);

	return usePriorityContent({
		isPriorityEnabled,
		priorities,
		canChangePriority,
		onClick: (priority, analyticsEvent) => onPriorityClick(priority, analyticsEvent),
	});
};
